/**
 * Where a Plan Line's bill rate came from, in words (RP-6.1).
 *
 * One place for the wording so the grid, the header, and the rate editor never describe the
 * same rate differently. Working-plan views only: the shared overview deliberately carries no
 * rate lineage (src/lib/plan-share-view.ts), so it never calls this.
 */

import type { PlanLineView, ResourcePlanDetailView } from "@/schemas/resource-plans";

export interface RateSourceInfo {
  /** Compact, for a narrow grid cell. */
  short: string;
  /** Full sentence, for a tooltip. */
  detail: string;
  /** True when the rate is anything other than the standard Rate Card's own figure. */
  departsFromStandard: boolean;
}

export function describeRateSource(
  line: Pick<PlanLineView, "rateSource" | "listRate">,
  plan: Pick<ResourcePlanDetailView, "rateBasisLabel" | "clientAccountName">,
): RateSourceInfo {
  switch (line.rateSource) {
    case "ClientDictated":
      return {
        short: "Client-set",
        detail:
          "Client-Dictated — no Rate Card lineage, but the line still maps to a Labor Category so cost and margin work",
        departsFromStandard: true,
      };
    case "AccountOverride":
      return {
        short: "Account",
        detail: `${plan.clientAccountName}'s negotiated Rate Card, on ${plan.rateBasisLabel}. It differs from the standard Rate Card.`,
        departsFromStandard: true,
      };
    case "LineOverride":
      return {
        short: "Custom",
        detail: `Custom rate on this line. The Rate Card rate on ${plan.rateBasisLabel} is ${line.listRate ?? "not set"}.`,
        departsFromStandard: true,
      };
    default:
      return {
        short: "Card",
        detail: `Standard Rate Card, on ${plan.rateBasisLabel}.`,
        departsFromStandard: false,
      };
  }
}
